import { z } from 'zod'

import { DateZod } from '../common'

export const SUPPORT_TITLE_MAX_LENGTH = 120
export const SUPPORT_PROVISIONAL_TITLE_MAX_LENGTH = 80
export const SUPPORT_TEXT_MAX_LENGTH = 4000
export const SUPPORT_DIAGNOSTIC_MAX_LENGTH = 20_000
export const SUPPORT_PROBABLE_CAUSE_MAX_LENGTH = 500
export const SUPPORT_FIRST_REQUEST_NUMBER = 1001
export const SUPPORT_RETENTION_DAYS = 30

// The security rules carry the same tokens inline: firestore.rules cannot import from a package.
export const SUPPORT_PERMISSION_BY_LINE = { l1: 'support_l1', l2: 'support_l2' } as const
export const SUPPORT_REQUESTER_PERMISSION = 'support_requester'
// A central administrator asks for support by default; the requester permission opens it to anyone else.
export const SUPPORT_REQUESTER_PERMISSIONS = ['central_admin', SUPPORT_REQUESTER_PERMISSION] as const

export const SupportCategoryZod = z.enum(['technical', 'usage', 'access', 'data', 'closure', 'other'])
export type SupportCategory = z.infer<typeof SupportCategoryZod>

export const SupportBlockingZod = z.enum(['canContinue', 'slowed', 'blocked'])
export type SupportBlocking = z.infer<typeof SupportBlockingZod>

export const SupportStatusZod = z.enum(['toHandle', 'waitingRequester', 'waitingValian', 'settled'])
export type SupportStatus = z.infer<typeof SupportStatusZod>

export const SupportRequesterStatusZod = z.enum(['open', 'waitingRequester', 'settled'])
export type SupportRequesterStatus = z.infer<typeof SupportRequesterStatusZod>

export const SupportTitleSourceZod = z.enum(['provisional', 'generated', 'renamed'])
export type SupportTitleSource = z.infer<typeof SupportTitleSourceZod>

export const SupportLineZod = z.enum(['l1', 'l2'])
export type SupportLine = z.infer<typeof SupportLineZod>

export const supportLineOf = (permissions: ReadonlyArray<string>): SupportLine | null => {
  if (permissions.includes(SUPPORT_PERMISSION_BY_LINE.l1)) {
    return 'l1'
  }
  return permissions.includes(SUPPORT_PERMISSION_BY_LINE.l2) ? 'l2' : null
}

export const canRequestSupport = (permissions: ReadonlyArray<string>, regionId: string | null | undefined) =>
  Boolean(regionId) &&
  supportLineOf(permissions) === null &&
  SUPPORT_REQUESTER_PERMISSIONS.some((permission) => permissions.includes(permission))

export const SupportPersonZod = z.object({
  piloteUserId: z.number(),
  firstName: z.string(),
  lastName: z.string(),
})
export type SupportPerson = z.infer<typeof SupportPersonZod>

export const SupportAuthorZod = SupportPersonZod.extend({ uid: z.string() })
export type SupportAuthor = z.infer<typeof SupportAuthorZod>

// `type` is Linear's state type (triage, backlog, unstarted, started, completed, canceled…), kept open so a
// type Linear adds later still parses.
export const SupportLinearStatusZod = z.object({
  name: z.string(),
  type: z.string(),
})
export type SupportLinearStatus = z.infer<typeof SupportLinearStatusZod>

export const SupportLinearTicketZod = z.object({
  id: z.string().nullish(),
  identifier: z.string(),
  url: z.string(),
  title: z.string(),
  status: SupportLinearStatusZod.nullish(),
})
export type SupportLinearTicket = z.infer<typeof SupportLinearTicketZod>

export const SupportContextZod = z.object({
  permissions: z.string().array(),
  centralName: z.string(),
  page: z.string(),
  object: z.object({ label: z.string(), url: z.string() }).nullable(),
  system: z.string(),
})
export type SupportContext = z.infer<typeof SupportContextZod>

export const SupportLastActivityZod = z.object({
  kind: z.enum(['agentReply', 'requesterMessage', 'settled']),
  at: DateZod,
})

export const SupportRequestZod = z.object({
  number: z.number().int(),
  regionId: z.string(),
  centralPiloteId: z.number().nullable(),
  centralName: z.string(),
  category: SupportCategoryZod,
  blocking: SupportBlockingZod,
  title: z.string().max(SUPPORT_TITLE_MAX_LENGTH),
  titleSource: SupportTitleSourceZod.nullish(),
  requesterStatus: SupportRequesterStatusZod,
  author: SupportAuthorZod,
  assignee: SupportPersonZod.nullable(),
  createdAt: DateZod,
  settledAt: DateZod.nullish(),
  lastAgentReplyAt: DateZod.nullish(),
  lastActivity: SupportLastActivityZod,
  context: SupportContextZod,
})
export type SupportRequest = z.infer<typeof SupportRequestZod>

const REQUESTER_STATUS_BY_STATUS = {
  toHandle: 'open',
  waitingRequester: 'waitingRequester',
  waitingValian: 'open',
  settled: 'settled',
} as const satisfies Record<SupportStatus, SupportRequesterStatus>

export const requesterStatusOf = (status: SupportStatus): SupportRequesterStatus => REQUESTER_STATUS_BY_STATUS[status]

export const valianAfterStatusesOf = (status: SupportStatus): SupportStatus[] =>
  status === 'waitingValian' ? ['waitingValian', 'toHandle'] : [status]
